import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { calculateFundingProgress } from "../services/ventureService";
/**
 * Component for displaying venture funding progress with a visual progress bar
 *
 * Requirements:
 * - 4.3: Display funding progress in venture views
 * - 7.1: Show current funds pledged and target goal
 * - 9.2: Display funding progress in public venture listings
 * - 10.2: Show funding progress in featured ventures
 *
 * Features:
 * - Responsive layout for mobile and desktop
 * - Black/white minimal design theme
 * - Percentage calculation
 * - Optional amount display
 * - Accessible with ARIA labels
 */
export const VentureFundingProgress = ({ fundsPledged, targetGoal, size = "md", showAmounts = true, className = "", }) => {
    // Calculate funding progress percentage (0-100)
    const progress = calculateFundingProgress(fundsPledged, targetGoal);
    // Format currency amounts for display
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    };
    // Size-based styling for progress bar height and text
    const sizeStyles = {
        sm: {
            barHeight: "h-1.5",
            textSize: "text-xs",
            spacing: "space-y-1",
        },
        md: {
            barHeight: "h-2",
            textSize: "text-sm",
            spacing: "space-y-2",
        },
        lg: {
            barHeight: "h-3",
            textSize: "text-base",
            spacing: "space-y-2",
        },
    };
    const styles = sizeStyles[size];
    return (_jsxs("div", { className: `w-full ${styles.spacing} ${className}`, role: "region", "aria-label": "Funding progress", children: [_jsx("div", { className: `w-full ${styles.barHeight} bg-gray-200 rounded-full overflow-hidden`, role: "progressbar", "aria-valuenow": progress, "aria-valuemin": 0, "aria-valuemax": 100, "aria-label": `${progress.toFixed(0)}% funded`, children: _jsx("div", { className: "h-full bg-black transition-all duration-300 ease-out", style: { width: `${progress}%` } }) }), showAmounts && (_jsxs("div", { className: `flex items-center justify-between ${styles.textSize} text-gray-700`, children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx("span", { className: "font-semibold text-black", children: formatCurrency(fundsPledged) }), _jsxs("span", { className: "text-gray-500", children: ["(", progress.toFixed(0), "%)"] })] }), _jsxs("div", { className: "text-gray-600", children: [_jsx("span", { className: "hidden sm:inline", children: "Goal: " }), _jsx("span", { className: "font-medium", children: formatCurrency(targetGoal) })] })] }))] }));
};
